import { useState, useEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Box, Theme, Typography, IconButton, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Mapping objects moved OUTSIDE the component to avoid recreation on every render
const MIME_TO_EXTENSION: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/msword': '.doc',
  'text/csv': '.csv',
  'image/png': '.png',
  'image/jpeg': '.jpg,.jpeg',
};

const MIME_TO_DISPLAY_NAME: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/msword': 'DOC',
  'text/csv': 'CSV',
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
};

const useStyles = makeStyles()((theme: Theme) => ({
  uploadBoxWrapper: {
    position: 'relative',
  },
  uploadBox: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    padding: theme.spacing(2),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    backgroundColor: theme.palette.primary.light,
    minHeight: '90px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      borderWidth: '2px',
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.primary.light,
    },
    '&.drag-over': {
      borderWidth: '2px',
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.primary.light,
    },
  },
  uploadLabel: {
    position: 'absolute',
    top: '-9px',
    left: '8px',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '14.4px',
    color: theme.palette.text.disabled,
    backgroundColor: theme.palette.primary.light,
    padding: '0 4px',
  },
  uploadIcon: {
    fontSize: '48px',
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(1.5),
  },
  uploadText: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  uploadSubText: {
    fontSize: '12px',
    color: theme.palette.text.disabled,
    marginTop: theme.spacing(0.5),
  },
  filePreview: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: '#333d4667',
    border: '1px solid #c9c2c271',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  fileDetails: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
  },
  fileIcon: {
    color: theme.palette.info.main,
    fontSize: '24px',
  },
  fileName: {
    fontSize: '14px',
    color: theme.palette.common.white,
    fontWeight: 500,
    wordBreak: 'break-all',
    whiteSpace: 'normal',
    lineHeight: 1.4,
    flex: 1,
    minWidth: 0,
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  iconButton: {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  errorText: {
    fontSize: '12px',
    color: theme.palette.error.main,
    marginTop: '8px',
  },
  fileSizeDisplay: {
    marginTop: '12px',
    fontSize: '14px',
    color: theme.palette.common.white,
    fontStyle: 'italic',
  },

}));

interface FormFileUploadFieldProps {
  label?: string;
  uploadLabel?: string;
  uploadText?: string;
  uploadSubText?: string;
  acceptedTypes?: string[];
  maxFileSize?: number; // in bytes
  onFileChange?: (file: File | null) => void;
  onRemoveFile?: () => void; // Optional callback when file is removed
  value?: File | null;
  disabled?: boolean;
  inputId?: string;
}

const FormFileUploadField = ({
  uploadLabel = 'Upload Invoice',
  uploadText = 'Click to upload or drag and drop',
  uploadSubText = 'PDF (max 10MB)',
  acceptedTypes = ['application/pdf'],
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  onFileChange,
  onRemoveFile,
  value,
  disabled = false,
  inputId = 'file-upload-input',
}: FormFileUploadFieldProps) => {
  const { classes } = useStyles();

  const [uploadedFile, setUploadedFile] = useState<File | null>(value || null);

  // Sync internal state with value prop
  useEffect(() => {
    setUploadedFile(value || null);
    // Clear the file input if value is null
    if (!value) {
      const fileInput = document.getElementById(inputId) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    }
  }, [value, inputId]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>('');

  const validateFile = (file: File): string | null => {
    if (!acceptedTypes.includes(file.type)) {
      const allowedNames = acceptedTypes.map(
        (t) => MIME_TO_DISPLAY_NAME[t] || t.split('/')[1].toUpperCase()
      );
      return `Only ${allowedNames.join(', ')} files are allowed`;
    }

    if (file.size > maxFileSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
      return `File size is ${fileSizeMB}MB. Maximum allowed size is ${maxSizeMB}MB.`;
    }

    return null;
  };

  const getAcceptString = () => {
    const extensions = acceptedTypes.map(
      (type) => MIME_TO_EXTENSION[type] || `.${type.split('/')[1]}`
    );
    return extensions.join(',');
  };

  const handleFileSelect = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setFileError(error);
      setUploadedFile(null);
      onFileChange?.(null);
    } else {
      setFileError('');
      setUploadedFile(file);
      onFileChange?.(file);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileError('');
    onFileChange?.(null);
    onRemoveFile?.();

    // Clear the file input
    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleDownloadFile = () => {
    if (uploadedFile) {
      const url = URL.createObjectURL(uploadedFile);
      const link = document.createElement('a');
      link.href = url;
      link.download = uploadedFile.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleBoxClick = () => {
    if (!disabled) {
      document.getElementById(inputId)?.click();
    }
  };

  return (
    <Box className={classes.uploadBoxWrapper}>
      <Typography className={classes.uploadLabel}>{uploadLabel}</Typography>
      <Box
        className={`${classes.uploadBox} ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleBoxClick}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <CloudUploadIcon className={classes.uploadIcon} />
        <Typography className={classes.uploadText}>{uploadText}</Typography>
        <Typography className={classes.uploadSubText}>{uploadSubText}</Typography>
        <input
          id={inputId}
          type="file"
          accept={getAcceptString()}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          disabled={disabled}
        />
      </Box>

      {uploadedFile && (
        <Box className={classes.filePreview}>
          <Box className={classes.fileDetails}>
            <InsertDriveFileIcon className={classes.fileIcon} />
            <Typography className={classes.fileName}>{uploadedFile.name}</Typography>
          </Box>
          <Box className={classes.actionButtons}>
            <Tooltip title="Download file">
              <IconButton
                size="small"
                onClick={handleDownloadFile}
                className={classes.iconButton}
                disabled={disabled}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete file">
              <IconButton
                size="small"
                onClick={handleRemoveFile}
                className={classes.iconButton}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {uploadedFile && (
        <div className={classes.fileSizeDisplay}>
          Selected: {uploadedFile.name} (
          {uploadedFile.size < 1000 * 1024
            ? `${(uploadedFile.size / 1024).toFixed(2)} KB`
            : `${(uploadedFile.size / 1024 / 1024).toFixed(2)} MB`}
          )
        </div>
      )}

      {fileError && <Typography className={classes.errorText}>{fileError}</Typography>}
    </Box>
  );
};

export default FormFileUploadField;